import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ChatMessage from './ChatMessage';
import { Message } from './types';
import { generateResponse } from './ai';

const ChatBot = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      type: 'bot',
      content: t('chatbot.welcome'),
      name: 'Benedikt Stein'
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Add user message
    const userMessage: Message = {
      type: 'user',
      content: input,
      name: t('chatbot.you')
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Generate bot response
    try {
      const response = await generateResponse(input);
      const botMessage: Message = {
        type: 'bot',
        content: response,
        name: 'Benedikt Stein'
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error generating response:', error);
      const errorMessage: Message = {
        type: 'bot',
        content: t('chatbot.error'),
        name: 'Benedikt Stein'
      };
      setMessages(prev => [...prev, errorMessage]);
    }
    setIsTyping(false);
  };

  return (
    <>
      {/* Chat Button */}
      <button
        id="chat-button"
        aria-label={t('chatbot.buttonLabel')}
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-4 right-4 p-3 sm:p-4 rounded-full transition-all duration-300 z-50 ${
          isOpen ? 'scale-0' : 'scale-100'
        }`}
        style={{ 
          backgroundColor: 'var(--button-bg-color)', 
          color: 'var(--button-text-color)', 
          border: '1px solid var(--color-white)' 
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--button-hover-bg-color)')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--button-bg-color)')}
      >
        <MessageSquare className="w-6 h-6" />
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-4 right-4 w-96 bg-white rounded-lg shadow-lg transition-all duration-300 z-50 ${
          isOpen ? 'scale-100' : 'scale-0'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-dark-green)'}}>
              <span className="font-semibold" 
                    style={{ backgroundColor: 'var(--color-dark-green)', color: 'var(--color-light-green)' }}>
                BS
              </span>
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-black)' }}>Benedikt Stein</h3>
              <p className="text-sm text-gray-500">{t('chatbot.title')}</p>
            </div>
          </div>
          <button
            id="close-button"
            aria-label={t('chatbot.closeLabel')}
            onClick={() => setIsOpen(false)}
            className="p-2 rounded-full transition-colors"
            style={{ backgroundColor: 'var(--button-bg-color)', color: 'var(--button-text-color)' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--button-hover-bg-color)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--button-bg-color)')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="h-96 overflow-y-auto p-4 space-y-4">
          {messages.map((message, index) => (
            <ChatMessage key={index} message={message} />
          ))}
          {isTyping && (
            <div className="flex items-center space-x-2 text-gray-500">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="border-t p-4">
          <div className="flex space-x-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('chatbot.placeholder')}
              className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': 'var(--color-light-green)' } as React.CSSProperties}
            />
            <button
              id="send-button"
              aria-label={t('chatbot.sendLabel')}
              type="submit"
              className="px-4 py-2 rounded-lg transition-colors"
              style={{ backgroundColor: 'var(--button-bg-color)', color: 'var(--button-text-color)' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--button-hover-bg-color)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--button-bg-color)')}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default ChatBot;